/**
 * Pure presentation of the Pitch save banner: one alert region, its message
 * and up to two actions. Copy verbatim from Figma 944:16259 except the live
 * names and the change summary. Visual parity against the frame is
 * NOT-VERIFIED until the M02 binding gate passes (SE-8232).
 * @module features/scouting/components/molecules/PitchSaveConflictBanner
 */
import React, { memo } from "react";

import { Box } from "@/components/atoms/Box";
import { Button } from "@/components/atoms/Button";
import { Stack } from "@/components/atoms/Stack";
import { Text } from "@/components/atoms/Text";

import { TEST_ID_PITCH_SAVE } from "e2e/testId";

import type { PitchSaveConflictBannerProps } from "./PitchSaveConflictBannerContainer";

/** Fallback editor name while no read exposes the last editor. */
const ANOTHER_EDITOR = "Another editor";

/** Surface per banner moment: warning for a live choice, error for a refusal. */
const SURFACE: Readonly<
  Record<PitchSaveConflictBannerProps["variant"], string>
> = {
  conflict: "rounded-sm border border-status-warning bg-status-warning-surface",
  failed: "rounded-sm border border-status-warning bg-status-warning-surface",
  refused: "rounded-sm border border-status-error bg-status-error-surface",
};

/**
 * The banner's message per variant.
 * @param props - The banner props
 * @returns The sentence(s) to show
 */
const messageFor = (props: PitchSaveConflictBannerProps): string => {
  if (props.variant === "conflict") {
    return `${props.editorName ?? ANOTHER_EDITOR} saved a newer version of ${props.listName} while you were editing. Your last change (${props.changeSummary}) was not saved.`;
  }
  if (props.variant === "failed") {
    return `Couldn't save after 3 tries. Your last change (${props.changeSummary}) is still on screen.`;
  }
  return `Couldn't re-apply: ${props.message}`;
};

/**
 * The banner's actions per variant: a quiet secondary and an accent primary.
 * @param props - The banner props
 * @returns The action row
 */
/**
 * The banner's test identities for its host (Pitch by default). Minted from
 * the registry's own stem per host, never by extending a `testID` prop
 * (SE-8049): on the Pitch route the Table stays mounted beside the Pitch.
 * @param props - The banner props
 * @returns The host's banner ids
 */
const idsFor = (props: PitchSaveConflictBannerProps) =>
  TEST_ID_PITCH_SAVE[props.surface ?? "pitch"].banner;

const actionsFor = (props: PitchSaveConflictBannerProps) => {
  const ids = idsFor(props);
  if (props.variant === "conflict") {
    return (
      <>
        <Button
          testID={ids.RELOAD}
          label="Reload their version"
          variant="outline"
          size="sm"
          shape="rounded"
          onPress={props.onReload}
          isDisabled={props.isBusy}
        />
        <Button
          testID={ids.REAPPLY}
          label="Re-apply my change"
          tone="accent"
          size="sm"
          shape="rounded"
          onPress={props.onReapply}
          isDisabled={props.isBusy}
        />
      </>
    );
  }
  if (props.variant === "failed") {
    return (
      <>
        <Button
          testID={ids.DISCARD}
          label="Discard"
          variant="outline"
          size="sm"
          shape="rounded"
          onPress={props.onDiscard}
          isDisabled={props.isBusy}
        />
        <Button
          testID={ids.RETRY}
          label="Retry"
          tone="accent"
          size="sm"
          shape="rounded"
          onPress={props.onRetry}
          isDisabled={props.isBusy}
        />
      </>
    );
  }
  return (
    <Button
      testID={ids.DISMISS}
      label="Dismiss"
      variant="outline"
      size="sm"
      shape="rounded"
      onPress={props.onDismiss}
    />
  );
};

/**
 * Renders the banner.
 * @param props - One of the three banner moments
 * @returns The rendered alert region
 */
const PitchSaveConflictBannerView = (props: PitchSaveConflictBannerProps) => (
  <Box
    testID={idsFor(props).ROOT}
    accessibilityRole="alert"
    className={`flex flex-col gap-3 px-4 py-3 ${SURFACE[props.variant]}`}
  >
    <Text
      testID={idsFor(props).MESSAGE}
      variant="body"
      className="text-content-primary"
    >
      {messageFor(props)}
    </Text>
    <Stack direction="horizontal" className="flex-wrap items-center gap-2">
      {actionsFor(props)}
    </Stack>
  </Box>
);

PitchSaveConflictBannerView.displayName = "PitchSaveConflictBannerView";
export default memo(PitchSaveConflictBannerView);
